import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import { UserPoolId, UserPoolClientId } from '../utilities/constants';

const userPool = new CognitoUserPool({
  UserPoolId: UserPoolId,
  ClientId: UserPoolClientId,
});

const AuthContext = createContext(null);

const buildUser = (session) => ({
  id_token: session.getIdToken().getJwtToken(),
  access_token: session.getAccessToken().getJwtToken(),
  profile: session.getIdToken().decodePayload(),
});

export function CognitoAuthProvider({ children }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  // Holds the in-flight CognitoUser + required attributes while a
  // NEW_PASSWORD_REQUIRED challenge is being completed by the login form.
  const [pendingNewPasswordUser, setPendingNewPasswordUser] = useState(null);

  useEffect(() => {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) {
      setIsLoading(false);
      return;
    }
    currentUser.getSession((err, session) => {
      if (err || !session?.isValid()) {
        setIsLoading(false);
        return;
      }
      setUser(buildUser(session));
      setIsAuthenticated(true);
      setIsLoading(false);
    });
  }, []);

  const signIn = useCallback((email, password) => {
    setError(null);
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.authenticateUser(
        new AuthenticationDetails({ Username: email, Password: password }),
        {
          onSuccess: (session) => {
            setUser(buildUser(session));
            setIsAuthenticated(true);
            resolve({ status: 'SUCCESS' });
          },
          onFailure: (err) => {
            setError(err);
            reject(err);
          },
          newPasswordRequired: (userAttributes) => {
            // Cognito returns these as non-editable attributes on the
            // challenge payload; they must be stripped before submitting
            // completeNewPasswordChallenge or Cognito rejects the call.
            delete userAttributes.email_verified;
            delete userAttributes.phone_number_verified;
            delete userAttributes.email;
            setPendingNewPasswordUser({ cognitoUser, userAttributes });
            resolve({ status: 'NEW_PASSWORD_REQUIRED' });
          },
        }
      );
    });
  }, []);

  const completeNewPassword = useCallback(
    (newPassword) => {
      return new Promise((resolve, reject) => {
        if (!pendingNewPasswordUser) {
          reject(new Error('No sign-in in progress.'));
          return;
        }
        pendingNewPasswordUser.cognitoUser.completeNewPasswordChallenge(
          newPassword,
          pendingNewPasswordUser.userAttributes,
          {
            onSuccess: (session) => {
              setUser(buildUser(session));
              setIsAuthenticated(true);
              setPendingNewPasswordUser(null);
              resolve();
            },
            onFailure: (err) => {
              setError(err);
              reject(err);
            },
          }
        );
      });
    },
    [pendingNewPasswordUser]
  );

  const forgotPassword = useCallback((email) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  }, []);

  const confirmForgotPassword = useCallback((email, code, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  }, []);

  const removeUser = useCallback(() => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) currentUser.signOut();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const value = {
    isLoading,
    isAuthenticated,
    user,
    error,
    signIn,
    completeNewPassword,
    forgotPassword,
    confirmForgotPassword,
    removeUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within a CognitoAuthProvider');
  }
  return ctx;
}
